import { FunctionComponent, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../generic";
import { auth, forms, dynamo } from "src/utils";
import { TextInputWithRegister } from "../../inputs";
import { AuthUser, useAuth } from "src/context/AuthContext";
import { userTypes } from "src/utils/types";
import SquareTile from "src/components/generic/tiles/SquareTile";

const LoginForm: FunctionComponent = () => {
  const navigate = useNavigate();
  const { user, setUser, setTokens } = useAuth();
  const [error, setError] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: yupResolver(forms.schema.LoginSchema),
  });

  const renavigate = (user: AuthUser) => {
    const goToClients =
      user.userType === userTypes.AccountTypes.OWNER ||
      user.userType === userTypes.AccountTypes.MANAGER ||
      user.userType === userTypes.AccountTypes.NEATT ||
      user.userType === userTypes.AccountTypes.REFERRAL ||
      user.userType === userTypes.AccountTypes.COMPANY;

    if (user.userType === userTypes.AccountTypes.CLIENT)
      navigate("/dashboard/neatt/action-items");
    else if (goToClients) navigate("/dashboard/consultant/clients");
    else navigate("/dashboard");
  };

  const submit: SubmitHandler<any> = async (data) => {
    const { email, password } = data;

    try {
      const { userId, accessToken, refreshToken, expiresIn, tokenType } =
        await auth.cognito.login(email, password);

      const { user } = await dynamo.fetch.userById(userId);
      const tokens = {
        accessToken,
        refreshToken,
        expiresIn,
        tokenType,
      };

      setUser(user);
      setTokens(tokens);

      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("tokens", JSON.stringify(tokens));

      renavigate(user);
    } catch (error: any) {
      setError(error?.message || "Login failed");
    }
  };

  useEffect(() => {
    if (user) renavigate(user);
  }, [user]);

  return (
    <form>
      <SquareTile styles="max-w-[600px] mx-auto flex flex-col items-center justify-around">
        {error && <p>{error}</p>}
        <TextInputWithRegister
          label="Email"
          id="email"
          placeholder="Email address..."
          containerStyles="lg:w-[85%]"
          register={register("email")}
          error={errors?.email?.message}
        />

        <TextInputWithRegister
          label="Password"
          id="password"
          placeholder="Enter strong password..."
          containerStyles="lg:w-[85%]"
          register={register("password")}
          password
          showPasswordToggle
          error={errors?.password?.message}
        />

        <Button text="Login" styles="my-4" onClick={handleSubmit(submit)} />

        <div className="flex flex-col">
          <Link to="/client-tiers" className="text-primary self-center mb-4">
            Need to Register?
          </Link>
          <Link to="/auth/forgot-password" className="text-primary self-center mb-4">
            Forgot Password
          </Link>
        </div>
      </SquareTile>
    </form>
  );
};

export default LoginForm;
